import { takeLatest, put, call } from 'redux-saga/effects';
import { Map } from 'immutable';

import * as apiKeysApi from 'src/service/api/apiKeys';
import * as apiKeysActions from './action';

import toast from 'src/utils/toast';
import { createUserFriendlyErrorMessage } from 'src/utils/utils';
import { getTranslation } from 'src/i18n/translations';

export function * apiKeysRoot () {
  yield takeLatest(apiKeysActions.FETCH_API_KEYS, fetchApiKeys);
  yield takeLatest(apiKeysActions.DELETE_API_KEY, deleteApiKey);
  yield takeLatest(apiKeysActions.CREATE_API_KEY, createApiKey);
  yield takeLatest(apiKeysActions.UPDATE_API_KEY, updateApiKey);
}

function * fetchApiKeys (action) {
  try {
    const apiKeysResponse = yield call(apiKeysApi.getApiKeys, action);

    // The API returns a paginated response with { data, total, totalPages }
    const keys = apiKeysResponse.get('data', Map([]));
    const total = apiKeysResponse.get('total', 0);
    const totalPages = apiKeysResponse.get('totalPages', 1);

    yield put(apiKeysActions.fetchApiKeysSuccess(keys, total, totalPages));
  } catch (err: any) {
    toast.error(createUserFriendlyErrorMessage(err, 'Error fetching API keys!'), err);
    yield put(apiKeysActions.fetchApiKeysFailed(Map(err)));
  }
}

function * deleteApiKey (action) {
  try {
    yield call(apiKeysApi.deleteApiKeyById, action);
    yield put(apiKeysActions.deleteApiKeySuccess(action.keyId));
    toast.success(getTranslation('toast.apiKeyDeleted'));
  } catch (err: any) {
    toast.error(createUserFriendlyErrorMessage(err, 'Error deleting API key'), err.response);
    yield put(apiKeysActions.deleteApiKeyFailed(Map(err)));
  }
}

function * createApiKey (action) {
  try {
    const response = yield call(apiKeysApi.createApiKey, action);

    // The API returns { id, token }, we need to transform it to match our expected structure
    const apiKey = Map({
      id: response.get('id'),
      created_at: new Date().toISOString(),
      last_used_at: null,
      permission_level: action.roles?.length > 0 ? action.roles[0] : 'default',
      name: action.name || null
    });

    yield put(apiKeysActions.createApiKeySuccess(apiKey, response.get('token')));
    toast.success(getTranslation('toast.apiKeyCreated'));
  } catch (err: any) {
    toast.error(createUserFriendlyErrorMessage(err, 'Error creating API key'), err.response);
    yield put(apiKeysActions.createApiKeyFailed(Map(err)));
  }
}

function * updateApiKey (action) {
  try {
    const response = yield call(apiKeysApi.updateApiKey, action);
    const apiKey = Map(response);

    yield put(apiKeysActions.updateApiKeySuccess(apiKey));
    toast.success(getTranslation('toast.apiKeyUpdated'));
    // Refresh the list to show updated data
    yield put(apiKeysActions.fetchApiKeys({ paginationDetails: {} }));
  } catch (err: any) {
    toast.error(createUserFriendlyErrorMessage(err, 'Error updating API key'), err.response);
    yield put(apiKeysActions.updateApiKeyFailed(Map(err)));
  }
}

export default fetchApiKeys;
